<template>
  <!-- v-if="state && hasRefinements" -->
  <div
    v-if="state"
    class="san-current-refinements"
    :class="[!hasRefinements && 'san-current-refinements--no-refinement']"
  >
    <slot
      :refine="state.refine"
      :items="state.items"
      :create-url="state.createURL"
    >
      <ul
        class="san-current-refinements--list"
        :class="listClass"
      >
        <li
          v-for="item in state.items"
          :key="item.attribute"
          class="san-current-refinements--item"
        >
          <slot
            name="item"
            :refine="item.refine"
            :item="item"
            :create-url="state.createURL"
          >
            <span class="san-current-refinements--label">{{ capitalize(item.label) }}: </span>
            <span
              v-for="refinement in item.refinements"
              :key="createItemKey(refinement)"
              class="san-current-refinements--category"
            >
              <slot
                name="refinement"
                :refine="item.refine"
                :refinement="refinement"
                :create-url="state.createURL"
              >
                <span class="san-current-refinements--category--label">
                  <q v-if="refinement.attribute === 'query'">{{
                    refinement.label
                  }}</q>
                  <template v-else>{{ refinement.label }}</template> </span><button
                  class="san-current-refinements--delete"
                  type="button"
                  @click.left.exact="item.refine(refinement)"
                >
                  ✕
                </button>
              </slot>
            </span>
          </slot>
        </li>
      </ul>
    </slot>
  </div>
</template>

<script setup>
import { inject, ref, computed, watch } from 'vue'
import { isEqual } from 'lodash'
import { connectCurrentRefinements } from 'instantsearch.js/es/connectors'
import { useConnector } from '@/composables'

defineOptions({ name: 'CurrentRefinements' })
const props = defineProps({
  includedAttributes: { type: Array, default: undefined },
  excludedAttributes: { type: Array, default: undefined },
  transformItems: { type: Function, default: undefined },
  listClass: { type: String, default: null }
})

const currentFilters = defineModel('currentFilters', { type: Array, default: () => [] })

const $emitter = inject('instantSearchPanelEmitter', { emit: () => {} })

const state = useConnector(connectCurrentRefinements, {
  $$widgetType: 'ais.currentRefinements',
  excludedAttributes: props.excludedAttributes,
  includedAttributes: props.includedAttributes,
  transformItems: props.transformItems
})

const hasEmitted = ref(false)

const hasRefinements = computed(() => state.value?.items?.length > 0)

const createItemKey = ({ attribute, value, type, operator }) => {
  return [attribute, type, value, operator].join(':')
}
const capitalize = value => {
  if (!value) return ''
  return (
    value.toString().charAt(0).toLocaleUpperCase()
    + value.toString().slice(1)
  )
}

const mapStateToCanRefine = s => !!s.canRefine

let previousComparableItems = null

watch(() => state.value, (newState, oldState) => {
  if (!newState) return

  // instantsearch.js re-renders the whole widget tree on every completed search (a single
  // filter click can trigger separate hits + facet-count round trips), so state.value gets a
  // new reference several times per interaction even when refinements haven't changed. Strip
  // the per-render `refine` closures before comparing so we only update the model on real changes.
  const comparableItems = newState.items.map(({ attribute, label, refinements }) => ({
    attribute,
    label,
    refinements: refinements.map(({ attribute, label, value, type, operator }) => ({ attribute, label, value, type, operator }))
  }))
  if (!isEqual(comparableItems, previousComparableItems)) {
    previousComparableItems = comparableItems
    currentFilters.value = newState.items
  }

  const previousCanRefine = mapStateToCanRefine(oldState || {})
  const nextCanRefine = mapStateToCanRefine(newState)

  if (!hasEmitted.value || previousCanRefine !== nextCanRefine) {
    $emitter.emit('PANEL_CHANGE_EVENT', nextCanRefine)
    hasEmitted.value = true
  }
}, { immediate: true })
</script>
